import { FC } from 'react'

import { useActiveProductsContext } from '~/context/ActiveProducts'
import { useBackdropsContext } from '~/context/Backdrops'
import { CombinedItemsProvider } from '~/context/CombinedItems'
import { useNavigation } from '~/context/Navigation'
import { useTryonContext } from '~/context/Tryon'

import { NewsletterContainer } from '~/containers/NewsletterContainer'

import { AlertSizing } from '~/components/AlertSizing'
import { BackdropSizing } from '~/components/BackdropSizing'
import { CombinedItemsBackdrop } from '~/components/CombinedItemsBackdrop'
import CouponFlowsBackdrop from '~/components/CouponFlowsBackdrop'
import { ProductsBackdrop } from '~/components/ProductsBackdrop'
import SettingsBackdrop from '~/components/SettingsBackdrop'
import { UnavailableToWearBackdrop } from '~/components/UnavailableToWearBackdrop'
import { UpscaleTag } from '~/components/UpscaleTag'

import { SetMeasurementsBackdrop } from '~/modules/Sizing/components/SetMeasurementsBackdrop'

import { OnboardingActiveScreen } from '~/screens/OnboardingActiveScreen'
import { OnboardingScreen } from '~/screens/OnboardingScreen'
import { setOnboardingAsViewed } from '~/utils/onboarding'
import { PostHogUtil } from '~/utils/postHog'

import { IStageBackdrops } from './types'

export const StageBackdrops: FC<IStageBackdrops> = ({
  screenLoading,
  handleAlertSizing,
  handleWalkthroughRestart,
  setIsMeasurementUpdatedManually,
}) => {
  const {
    isCombinedItemsVisible,
    handleCombinedItemsState,
    isCombineVisible,
    setIsCombineVisible,
    isAlertSizing,
    isSettingsVisible,
    setIsSettingsVisible,
    isAlertMeasurements,
    setIsAlertMeasurements,
    isOnboardingScreenVisible,
    setIsOnboardingScreenVisible,
    isOnboardingActiveScreenVisible,
    setIsOnboardingActiveScreenVisible,
    onOpenNewsletter,
    isSendMeasurementsBackdropOpen,
    setIsSendMeasurementsBackdropOpen,
    isUnavailableToWearBackdropVisible,
    setIsUnavailableToWearBackdropVisible,
  } = useBackdropsContext()
  const { stateTryon, stateCurrentTryon, stateTryonUpscale } = useTryonContext()
  const { navigate } = useNavigation()
  const { products: activeProducts, clearActiveProducts } = useActiveProductsContext()

  const IS_NEWSLETTER_FLAG_ENABLED = PostHogUtil.isFlagEnabled('NEWSLETTER_CONTENT')
  const IS_COUPON_FLOW_FLAG_ENABLED = PostHogUtil.isFlagEnabled('COUPON_FLOW')

  const haveProductWithSizeAndNoViton = activeProducts.find(p => p.has_size && !p.has_viton)

  const handleWalkthrough = () => {
    setIsSettingsVisible(false)
    handleWalkthroughRestart()
  }

  const handleMeasurementsAlertRedoPhotoButton = () => navigate('Tips')

  const handleMeasurementsAlertButtonLater = () => setIsAlertMeasurements(false)

  const handleNavigateToSizingScreen = () => {
    setIsUnavailableToWearBackdropVisible(false)
    navigate('SizingSuggestionScreen')
  }

  const handleChangeProduct = () => {
    clearActiveProducts()
    setIsUnavailableToWearBackdropVisible(false)
  }

  return (
    <>
      <CombinedItemsProvider>
        <CombinedItemsBackdrop
          active={isCombinedItemsVisible}
          handleClose={() => handleCombinedItemsState(false)}
          products={{
            top: stateCurrentTryon?.products?.top?.identifier,
            bottom: stateCurrentTryon?.products?.bottom?.identifier,
            full: stateCurrentTryon?.products?.full?.identifier,
          }}
        />
      </CombinedItemsProvider>
      <ProductsBackdrop active={isCombineVisible} handleState={setIsCombineVisible} isLoading={screenLoading} />
      <AlertSizing visible={isAlertSizing} onClick={handleAlertSizing} />
      <SettingsBackdrop
        active={isSettingsVisible}
        handleState={setIsSettingsVisible}
        handleWalkthrough={handleWalkthrough}
      />
      <BackdropSizing
        visible={isAlertMeasurements}
        outlinedButtonClick={handleMeasurementsAlertButtonLater}
        primaryButtonClick={handleMeasurementsAlertRedoPhotoButton}
      />

      <OnboardingScreen
        visible={isOnboardingScreenVisible}
        handleClose={() => {
          setOnboardingAsViewed()

          setIsOnboardingScreenVisible(false)

          if (activeProducts.find(p => p.has_viton)) return

          setIsUnavailableToWearBackdropVisible(true)
        }}
        handleStartOnboarding={() => {
          setIsOnboardingActiveScreenVisible(true)
          setIsOnboardingScreenVisible(false)
        }}
      />

      <OnboardingActiveScreen
        visible={isOnboardingActiveScreenVisible}
        handleClose={() => setIsOnboardingActiveScreenVisible(false)}
      />

      <NewsletterContainer
        onClickSnackBar={onOpenNewsletter}
        blockingState={stateTryon?.isLoading || screenLoading || !IS_NEWSLETTER_FLAG_ENABLED}
      />

      {IS_COUPON_FLOW_FLAG_ENABLED && <CouponFlowsBackdrop />}

      <SetMeasurementsBackdrop
        visible={isSendMeasurementsBackdropOpen}
        handleClose={() => setIsSendMeasurementsBackdropOpen(false)}
        onSuccessCallback={() => {
          localStorage.setItem('@doris:child-form-filled', JSON.stringify(true))
          setIsMeasurementUpdatedManually()
        }}
      />

      {haveProductWithSizeAndNoViton && (
        <UnavailableToWearBackdrop
          visible={isUnavailableToWearBackdropVisible}
          thumbnail={haveProductWithSizeAndNoViton.thumbnail}
          identifier={haveProductWithSizeAndNoViton.identifier}
          handleNavigate={handleNavigateToSizingScreen}
          handleChangeProduct={handleChangeProduct}
        />
      )}

      {!screenLoading && stateTryonUpscale?.isLoading && <UpscaleTag />}
    </>
  )
}
